<template>
  <IntercomLayout>
    <template #topbar>
      <SevaTopbar
        :business-name="businessName"
        :user="user"
        :global-user="globalUser"
        @settings="$router.push('/settings')"
        @logout="handleLogout"
      />
    </template>

    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <div class="activity-log-view">
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h1 class="page-title">{{ $t('activityLog.title') }}</h1>
          <p class="page-subtitle">{{ $t('activityLog.subtitle') }}</p>
        </div>
        <IntercomButton variant="primary" @click="openAddModal" :disabled="!selectedProgram">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          <span>{{ $t('activityLog.addEntry') }}</span>
        </IntercomButton>
      </div>

      <!-- Program Selector -->
      <div class="program-selector-row">
        <div class="program-selector">
          <IntercomSelect
            v-model="selectedProgramId"
            :label="$t('activityLog.selectProgram')"
            :options="programOptions"
            :placeholder="$t('activityLog.selectProgramPlaceholder')"
            :loading="isProgramsLoading"
            @update:model-value="handleProgramChange"
          />
        </div>
        <IntercomButton
          variant="ghost"
          size="sm"
          @click="$router.push('/programs')"
        >
          {{ $t('program.manage') }}
        </IntercomButton>
      </div>

      <!-- Summary Cards (shown when a program is selected) -->
      <div v-if="selectedProgram" class="summary-row">
        <IntercomCard class="summary-card">
          <div class="summary-label">{{ $t('activityLog.totalMetric', { label: selectedProgram.metric_label }) }}</div>
          <div class="summary-value">{{ formatNumber(summary.totalMetric) }}</div>
          <div class="summary-period">{{ $t('dailyStats.thisMonth') }}</div>
        </IntercomCard>
        <IntercomCard class="summary-card">
          <div class="summary-label">{{ $t('activityLog.totalExpense') }}</div>
          <div class="summary-value">{{ formatCompactCurrency(summary.totalExpense) }}</div>
          <div class="summary-period">{{ $t('dailyStats.thisMonth') }}</div>
        </IntercomCard>
        <IntercomCard class="summary-card">
          <div class="summary-label">{{ $t('activityLog.avgCostPerUnit', { unit: selectedProgram.metric_unit }) }}</div>
          <div class="summary-value">{{ formatCompactCurrency(summary.avgCostPerUnit) }}</div>
          <div class="summary-period">{{ $t('dailyStats.thisMonth') }}</div>
        </IntercomCard>
      </div>

      <!-- Date Filters -->
      <div v-if="selectedProgram" class="filters-row">
        <IntercomDateInput
          v-model="filterDateFrom"
          class="date-input"
          @update:model-value="fetchLogs"
        />
        <span class="date-separator">{{ $t('filters.to') }}</span>
        <IntercomDateInput
          v-model="filterDateTo"
          class="date-input"
          @update:model-value="fetchLogs"
        />
      </div>

      <!-- Logs Table -->
      <IntercomCard v-if="selectedProgram">
        <IntercomTable
          :columns="activeColumns"
          :data="logs"
          :loading="isLoading"
          hoverable
          clickable
          @row-click="handleRowClick"
        >
          <template #cell-activity_date="{ value }">
            {{ formatDate(value) }}
          </template>
          <template #cell-metric_value="{ value }">
            <span class="metric-value">{{ formatNumber(value || 0) }}</span>
          </template>
          <template #cell-total_expense="{ value }">
            {{ formatCompactCurrency(value || 0) }}
          </template>
          <template #cell-cost_per_unit="{ value }">
            {{ formatCompactCurrency(value || 0) }}
          </template>
        </IntercomTable>
        <p v-if="!isLoading && logs.length === 0" class="empty-text">
          {{ $t('activityLog.noEntries') }}
        </p>
      </IntercomCard>

      <!-- No program selected state -->
      <IntercomCard v-else-if="!isProgramsLoading">
        <p class="empty-text">{{ $t('activityLog.selectProgramPrompt') }}</p>
      </IntercomCard>

      <!-- Add/Edit Modal -->
      <IntercomModal
        v-model:show="showModal"
        :title="editingEntry ? $t('activityLog.editEntry') : $t('activityLog.addEntry')"
      >
        <ActivityLogForm
          :entry="editingEntry"
          :date="selectedDate"
          :programs="programs"
          :fixed-program="selectedProgram"
          :loading="isSaving"
          @submit="handleSave"
          @cancel="closeModal"
        />
      </IntercomModal>
    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useToast } from '@shared/composables/useToast.js'
import { useAuth } from '@shared/composables/useAuth.js'
import { useBusiness } from '@shared/composables/useBusiness.js'
import { useSeva } from '../composables/useSeva.js'
import { formatCompactCurrency } from '@shared/utils/currency.js'
import {
  IntercomLayout,
  IntercomSidebar,
  IntercomButton,
  IntercomCard,
  IntercomTable,
  IntercomInput,
  IntercomDateInput,
  IntercomSelect,
  IntercomModal,
} from '@shared/components/intercom'
import SevaTopbar from '../components/SevaTopbar.vue'
import ActivityLogForm from '../components/ActivityLogForm.vue'
import { useNavigation } from '../composables/useNavigation.js'
import dayjs from 'dayjs'

const { t } = useI18n()
const router = useRouter()
const toast = useToast()
const { user, globalUser, logout } = useAuth()
const { businessName, fetchBusiness } = useBusiness()
const { listPrograms, listActivityLogs, upsertActivityLog } = useSeva()
const { navSections } = useNavigation()

const programs = ref([])
const logs = ref([])
const isProgramsLoading = ref(false)
const isLoading = ref(false)
const isSaving = ref(false)
const selectedProgramId = ref(null)
const filterDateFrom = ref(dayjs().startOf('month').format('YYYY-MM-DD'))
const filterDateTo = ref(dayjs().format('YYYY-MM-DD'))
const showModal = ref(false)
const editingEntry = ref(null)
const selectedDate = ref(dayjs().format('YYYY-MM-DD'))

const formatDate = (date) => date ? dayjs(date).format('DD MMM YYYY') : ''
const formatNumber = (num) => num ? num.toLocaleString('en-IN') : '0'

const selectedProgram = computed(() =>
  programs.value.find(p => p.id === selectedProgramId.value) || null
)

const programOptions = computed(() =>
  programs.value
    .filter(p => p.is_active)
    .map(p => ({ value: p.id, label: p.name }))
)

// Dynamic table columns — metric column header comes from selected program
const activeColumns = computed(() => [
  { key: 'activity_date', title: t('activityLog.date'), width: '140px' },
  {
    key: 'metric_value',
    title: selectedProgram.value?.metric_label || t('activityLog.metricValue'),
    width: '160px',
    align: 'right',
  },
  { key: 'total_expense', title: t('activityLog.totalExpense'), width: '140px', align: 'right' },
  {
    key: 'cost_per_unit',
    title: t('activityLog.costPerUnit', { unit: selectedProgram.value?.metric_unit || 'unit' }),
    width: '140px',
    align: 'right',
  },
  { key: 'notes', title: t('activityLog.notes'), width: '200px' },
])

const summary = computed(() => {
  const totalMetric = logs.value.reduce((sum, l) => sum + (l.metric_value || 0), 0)
  const totalExpense = logs.value.reduce((sum, l) => sum + Number(l.total_expense || 0), 0)
  const avgCostPerUnit = totalMetric > 0 ? totalExpense / totalMetric : 0
  return { totalMetric, totalExpense, avgCostPerUnit }
})

const fetchPrograms = async () => {
  isProgramsLoading.value = true
  try {
    const response = await listPrograms()
    programs.value = response?.programs || []
    // Auto-select the default program if available
    const defaultProgram = programs.value.find(p => p.is_default)
    if (defaultProgram && !selectedProgramId.value) {
      selectedProgramId.value = defaultProgram.id
      fetchLogs()
    }
  } catch (error) {
    console.error('Failed to fetch programs:', error)
    toast.error(t('errors.loadFailed'))
  } finally {
    isProgramsLoading.value = false
  }
}

const fetchLogs = async () => {
  if (!selectedProgramId.value) return
  isLoading.value = true
  try {
    const params = { program_id: selectedProgramId.value }
    if (filterDateFrom.value) params.date_from = filterDateFrom.value
    if (filterDateTo.value) params.date_to = filterDateTo.value
    const response = await listActivityLogs(params)
    logs.value = response?.logs || []
  } catch (error) {
    console.error('Failed to fetch activity logs:', error)
    toast.error(t('errors.loadFailed'))
  } finally {
    isLoading.value = false
  }
}

const handleProgramChange = () => {
  logs.value = []
  fetchLogs()
}

const openAddModal = () => {
  editingEntry.value = null
  selectedDate.value = dayjs().format('YYYY-MM-DD')
  showModal.value = true
}

const handleRowClick = (entry) => {
  editingEntry.value = entry
  showModal.value = true
}

const handleSave = async (formData) => {
  isSaving.value = true
  try {
    await upsertActivityLog(formData)
    toast.success(editingEntry.value ? t('success.activityLogUpdated') : t('success.activityLogCreated'))
    closeModal()
    fetchLogs()
  } catch (error) {
    console.error('Failed to save activity log:', error)
    toast.error(t('errors.saveFailed'))
  } finally {
    isSaving.value = false
  }
}

const closeModal = () => {
  showModal.value = false
  editingEntry.value = null
}

const handleLogout = async () => {
  await logout()
  router.push('/login')
}

onMounted(() => {
  fetchBusiness()
  fetchPrograms()
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';

.activity-log-view {
  padding: var(--space-8);
  max-width: var(--content-max-width);
  margin: 0 auto;
}

.page-header {
  margin-bottom: var(--space-6);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  margin: 0;
}

.page-subtitle {
  font-size: var(--font-size-base);
  color: var(--color-gray-500);
  margin-top: var(--space-1);
  margin-bottom: 0;
}

.program-selector-row {
  display: flex;
  align-items: flex-end;
  gap: var(--space-4);
  margin-bottom: var(--space-6);
}

.program-selector {
  width: 300px;
}

.summary-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-4);
  margin-bottom: var(--space-6);
}

.summary-card {
  padding: var(--space-4);
  text-align: center;
}

.summary-label {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
  margin-bottom: var(--space-1);
}

.summary-value {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
}

.summary-period {
  font-size: var(--font-size-xs);
  color: var(--color-gray-400);
  margin-top: var(--space-1);
}

.filters-row {
  display: flex;
  gap: var(--space-4);
  margin-bottom: var(--space-6);
  align-items: center;
}

.date-input {
  width: 150px;
}

.date-separator {
  color: var(--color-gray-500);
}

.metric-value {
  font-weight: var(--font-weight-semibold);
}

.empty-text {
  text-align: center;
  color: var(--color-gray-500);
  padding: var(--space-8);
}

@media (max-width: 768px) {
  .activity-log-view {
    padding: var(--space-4);
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-4);
  }

  .program-selector-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .program-selector {
    width: 100%;
  }

  .summary-row {
    grid-template-columns: 1fr;
  }

  .filters-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .date-input {
    width: 100%;
  }
}
</style>
